<template>
  <f7-list form>
    <f7-list-input
      ref="inputUsernameRef"
      v-model:value="form.username"
      :label="t('common.user')"
      required
      type="text"
      :placeholder="t('login.enterYourUsername')"
    />

    <f7-list-input
      v-model:value="form.password"
      :label="t('common.password')"
      required
      type="password"
      autocomplete="off"
      :placeholder="t('login.enterYourPassword')"
      @keyup.enter="validateCredentials"
    />
  </f7-list>

  <f7-button
    large
    fill
    @click="validateCredentials"
  >
    {{ t('common.apply') }}
  </f7-button>
</template>

<script setup>
// @ts-check
import { showToast } from '@/js/common/HiNotificationExSupport';
import { HiDialog } from '@/js/common/HINotificationSupport';
import { callValidateInternalCredentials } from '@/js/services/APIErp';
import { logError } from '@/js/utils/log';
import { sleep } from '@/js/utils/sleep';
import { reactive, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';

/** @import { ShallowRef } from 'vue' */

const props = defineProps({
  autofocus: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits([
  'success',
]);

const { t } = useI18n();
const form = reactive({
  username: '',
  password: '',
});

/** @type {ShallowRef<InstanceType<typeof import('framework7-vue')['f7ListInput']> | null>} */
const inputUsernameRef = shallowRef(null);
watch(() => props.autofocus, async (val) => {
  if (val) {
    await sleep(150);
    focusOnUsernameInput();
  }
}, { immediate: true });

function focusOnUsernameInput() {
  const inputEl = inputUsernameRef.value?.$el.querySelector('input');
  inputEl?.focus();
}

/**
 * @param {string} text
 * @returns {void}
 */
function showInvalidDataToast(text) {
  showToast({
    title: t('internalConfigurations.info.incorrectCredentials'),
    text,
    type: 'danger',
    typeIcon: true,
  });
}

function checkDataValid() {
  if (!form.username) {
    showInvalidDataToast(t('internalConfigurations.info.usernameRequired'));
    return false;
  }

  if (!form.password) {
    showInvalidDataToast(t('internalConfigurations.info.passwordRequired'));
    return false;
  }

  return true;
}

async function validateCredentials() {
  if (!checkDataValid()) {
    return;
  }

  HiDialog.Preloader(true, t('internalConfigurations.info.validatingCredentials'));

  try {
    const { data } = await callValidateInternalCredentials({
      username: form.username,
      password: form.password,
    });

    if (data.Valor) {
      emit('success');
    } else {
      showInvalidDataToast(t('common.info.invalidUsernameOrPassword'));
    }
  } catch (error) {
    logError(error);
    showInvalidDataToast(t('common.checkYourConnection'));
  }

  HiDialog.Preloader(false);
}
</script>
